import { useState } from "react";
import { POSITIONS, OUTWORDS, SITUATION_TYPES, inputStyle } from "../constants";

const RUNNER_OPTS = [
  { id:"first",  label:"1st" },
  { id:"second", label:"2nd" },
  { id:"third",  label:"3rd" },
];

const emptyScenario = {
  title:"", position:"", situationType:"", inning:1, half:"top", teamScore:0, opponentScore:0,
  outs:0, runners:[], situation:"", ballInPlay:"", specialNote:"",
  question:"", choices:["","","",""], correctIndices:[], explanation:"",
};

// ── Scenario Form ──────────────────────────────────────────────────────────
// Coach-facing builder for a custom scenario. Controlled form; parent owns
// persistence (create vs. update) via onSave.

export function ScenarioForm({ initial, onSave, onCancel }) {
  const [s, setS] = useState(() => ({ ...emptyScenario, ...(initial||{}) }));
  const [error, setError] = useState(null);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const setChoice = (i, v) => setS(prev => ({ ...prev, choices: prev.choices.map((c,idx)=>idx===i?v:c) }));
  const toggleRunner = id => setS(prev => ({ ...prev, runners: prev.runners.includes(id) ? prev.runners.filter(r=>r!==id) : [...prev.runners, id] }));
  const toggleCorrect = i => setS(prev => ({ ...prev, correctIndices: prev.correctIndices.includes(i) ? prev.correctIndices.filter(x=>x!==i) : [...prev.correctIndices, i].sort() }));

  const handleSave = () => {
    if (!s.title.trim())                                  return setError("Give the scenario a short title.");
    if (!s.situation.trim() || !s.ballInPlay.trim())       return setError("Situation and ball-in-play are required.");
    if (!s.question.trim())                                return setError("Add a question for the player.");
    if (s.choices.some(c=>!c.trim()))                      return setError("All 4 answer choices need text.");
    if (s.correctIndices.length===0)                       return setError("Mark at least one choice as correct.");
    if (!s.explanation.trim())                              return setError("Add an explanation for the answer.");
    setError(null);
    onSave({ ...s, inning:Number(s.inning), teamScore:Number(s.teamScore), opponentScore:Number(s.opponentScore), outs:Number(s.outs) });
  };

  return (
    <div style={{background:"white",border:"1.5px solid #231F20",borderRadius:10,padding:"16px",marginBottom:14}}>
      <div style={{fontSize:14,fontWeight:700,color:"#231F20",marginBottom:12}}>{initial?"Edit scenario":"New scenario"}</div>

      <label style={labelStyle}>Title (for your reference only)</label>
      <input style={inputStyle} value={s.title} onChange={e=>set("title",e.target.value)} placeholder="e.g. Bunt with runner on 3rd, 1 out"/>

      <label style={labelStyle}>Position (optional — leave blank for any)</label>
      <select style={inputStyle} value={s.position} onChange={e=>set("position",e.target.value)}>
        <option value="">Any position</option>
        {POSITIONS.map(p=><option key={p.id} value={p.id}>{p.id} — {p.label}</option>)}
      </select>

      <label style={labelStyle}>Situation type (optional — powers weakness tracking)</label>
      <select style={inputStyle} value={s.situationType} onChange={e=>set("situationType",e.target.value)}>
        <option value="">Unclassified</option>
        {SITUATION_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
      </select>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div>
          <label style={labelStyle}>Inning</label>
          <input style={inputStyle} type="number" min="1" value={s.inning} onChange={e=>set("inning",e.target.value)}/>
        </div>
        <div>
          <label style={labelStyle}>Half</label>
          <select style={inputStyle} value={s.half} onChange={e=>set("half",e.target.value)}>
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Team score</label>
          <input style={inputStyle} type="number" min="0" value={s.teamScore} onChange={e=>set("teamScore",e.target.value)}/>
        </div>
        <div>
          <label style={labelStyle}>Opponent score</label>
          <input style={inputStyle} type="number" min="0" value={s.opponentScore} onChange={e=>set("opponentScore",e.target.value)}/>
        </div>
      </div>

      <label style={labelStyle}>Outs</label>
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        {OUTWORDS.map((w,i)=>(
          <button key={i} onClick={()=>set("outs",i)} type="button" style={pillStyle(s.outs===i)}>{w}</button>
        ))}
      </div>

      <label style={labelStyle}>Runners on base</label>
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        {RUNNER_OPTS.map(r=>(
          <button key={r.id} onClick={()=>toggleRunner(r.id)} type="button" style={pillStyle(s.runners.includes(r.id))}>{r.label}</button>
        ))}
      </div>

      <label style={labelStyle}>Situation (setup before the pitch)</label>
      <textarea style={{...inputStyle,minHeight:60}} value={s.situation} onChange={e=>set("situation",e.target.value)} placeholder="Describe outs, runners, score context..."/>

      <label style={labelStyle}>Ball in play (what happens)</label>
      <textarea style={{...inputStyle,minHeight:50}} value={s.ballInPlay} onChange={e=>set("ballInPlay",e.target.value)} placeholder="e.g. A sharp grounder is hit right at you."/>

      <label style={labelStyle}>Key situation note (optional)</label>
      <input style={inputStyle} value={s.specialNote} onChange={e=>set("specialNote",e.target.value)} placeholder="Anything that changes the read..."/>

      <label style={labelStyle}>Question</label>
      <input style={inputStyle} value={s.question} onChange={e=>set("question",e.target.value)} placeholder="What's the best play?"/>

      <label style={labelStyle}>Answer choices — check the box next to the correct one(s)</label>
      {s.choices.map((c,i)=>(
        <div key={i} style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
          <input type="checkbox" checked={s.correctIndices.includes(i)} onChange={()=>toggleCorrect(i)} style={{width:18,height:18,flexShrink:0,cursor:"pointer"}}/>
          <input style={{...inputStyle,marginBottom:0}} value={c} onChange={e=>setChoice(i,e.target.value)} placeholder={`Choice ${String.fromCharCode(65+i)}`}/>
        </div>
      ))}

      <label style={labelStyle}>Explanation (shown after the player answers)</label>
      <textarea style={{...inputStyle,minHeight:60}} value={s.explanation} onChange={e=>set("explanation",e.target.value)} placeholder="Encouraging, 2-3 sentence explanation of the correct play..."/>

      {error && <div style={{color:"#e53935",fontSize:12,marginBottom:10}}>{error}</div>}

      <div style={{display:"flex",gap:8}}>
        <button onClick={handleSave} style={{flex:1,padding:"11px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:7,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"monospace"}}>Save Scenario</button>
        <button onClick={onCancel} style={{padding:"11px 16px",background:"white",color:"#888",border:"1px solid #ddd",borderRadius:7,cursor:"pointer",fontSize:13}}>Cancel</button>
      </div>
    </div>
  );
}

const labelStyle = { display:"block", fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:5 };

const pillStyle = active => ({
  padding:"7px 12px", borderRadius:999, border:active?"2px solid #231F20":"1px solid #ddd",
  background:active?"#231F20":"white", color:active?"#1FD71D":"#333", cursor:"pointer",
  fontSize:11, fontWeight:700, fontFamily:"monospace",
});
